import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getGroomingSchedule, createGroomingTask, logGrooming, getGroomingLogs, extractErrorMessage } from '../services/api';
import { FaPlus, FaCheck, FaCut, FaClipboardList } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function GroomingSchedule({ user }) {
  const toast = useToast();
  const [schedule, setSchedule] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [formErrorTask, setFormErrorTask] = useState(null);
  const [savingLog, setSavingLog] = useState(false);
  const [formErrorLog, setFormErrorLog] = useState(null);
  const [newTask, setNewTask] = useState({
    user_id: '',
    task: '',
    frequency: 'daily',
    day_of_week: '',
    time_of_day: '',
    notes: ''
  });
  const [newLog, setNewLog] = useState({
    user_id: '',
    task_id: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [scheduleData, logsData] = await Promise.all([
        getGroomingSchedule(),
        getGroomingLogs()
      ]);
      setSchedule(scheduleData.schedule || scheduleData);
      setLogs(logsData.logs || logsData);
    } catch (error) {
      console.error('Error fetching grooming data:', error);
      toast.error(extractErrorMessage(error, 'Grooming-Daten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (savingTask) return;
    setSavingTask(true);
    setFormErrorTask(null);
    try {
      await createGroomingTask(newTask);
      setNewTask({ user_id: '', task: '', frequency: 'daily', day_of_week: '', time_of_day: '', notes: '' });
      setShowTaskModal(false);
      toast.success('Grooming-Aufgabe erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating grooming task:', error);
      setFormErrorTask(extractErrorMessage(error, 'Fehler beim Erstellen der Grooming-Aufgabe'));
    } finally {
      setSavingTask(false);
    }
  };

  const handleLogGrooming = async (e) => {
    e.preventDefault();
    if (savingLog) return;
    setSavingLog(true);
    setFormErrorLog(null);
    try {
      await logGrooming(newLog);
      setNewLog({ user_id: '', task_id: '', notes: '' });
      setShowLogModal(false);
      toast.success('Grooming-Eintrag gespeichert');
      fetchData();
    } catch (error) {
      console.error('Error logging grooming:', error);
      setFormErrorLog(extractErrorMessage(error, 'Fehler beim Loggen der Grooming-Aufgabe'));
    } finally {
      setSavingLog(false);
    }
  };

  const closeTaskModal = () => {
    if (savingTask) return;
    setShowTaskModal(false);
    setFormErrorTask(null);
  };

  const closeLogModal = () => {
    if (savingLog) return;
    setShowLogModal(false);
    setFormErrorLog(null);
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      daily: '#43b581',
      weekly: '#7289da',
      biweekly: '#faa61a',
      monthly: '#f04747'
    };
    return (
      <span style={{
        background: colors[frequency] || '#99aab5',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.85em'
      }}>
        {frequency}
      </span>
    );
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        {/* Schedule Section */}
        <div className="page-header">
          <h1><FaCut /> Grooming Schedule</h1>
          <button className="add-btn" onClick={() => setShowTaskModal(true)}>
            <FaPlus /> New Task
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Task</th>
                    <th>Frequency</th>
                    <th>Day</th>
                    <th>Time</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.length > 0 ? (
                    schedule.map(task => (
                      <tr key={task.id}>
                        <td>{task.id}</td>
                        <td>{task.user_id || '-'}</td>
                        <td>{task.task}</td>
                        <td>{getFrequencyBadge(task.frequency)}</td>
                        <td>{task.day_of_week || '-'}</td>
                        <td>{task.time_of_day || '-'}</td>
                        <td>{task.notes || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">No grooming tasks scheduled</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Logs Section */}
            <div className="page-header" style={{ marginTop: '2rem' }}>
              <h2><FaClipboardList /> Grooming Logs</h2>
              <button className="add-btn" onClick={() => setShowLogModal(true)}>
                <FaCheck /> Log Completion
              </button>
            </div>

            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>User</th>
                    <th>Task ID</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length > 0 ? (
                    logs.map(log => (
                      <tr key={log.id}>
                        <td>
                          {new Date(log.created_at).toLocaleDateString('de-DE', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </td>
                        <td>{log.user_id || '-'}</td>
                        <td>{log.task_id}</td>
                        <td>{log.notes || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">No grooming logs recorded</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* New Task Modal */}
        {showTaskModal && (
          <div className="modal-overlay" onClick={closeTaskModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>New Grooming Task</h2>
              {formErrorTask && <div className="shared-form-error-banner">{formErrorTask}</div>}
              <form onSubmit={handleCreateTask}>
                <div className="form-group">
                  <UserSelect
                    value={newTask.user_id}
                    onChange={id => setNewTask({...newTask, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Task</label>
                  <input
                    type="text"
                    value={newTask.task}
                    onChange={e => setNewTask({...newTask, task: e.target.value})}
                    placeholder="e.g., Shave, Nails, Hair care"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    value={newTask.frequency}
                    onChange={e => setNewTask({...newTask, frequency: e.target.value})}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Day of Week</label>
                  <select
                    value={newTask.day_of_week}
                    onChange={e => setNewTask({...newTask, day_of_week: e.target.value})}
                  >
                    <option value="">Any</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Time of Day</label>
                  <input
                    type="time"
                    value={newTask.time_of_day}
                    onChange={e => setNewTask({...newTask, time_of_day: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newTask.notes}
                    onChange={e => setNewTask({...newTask, notes: e.target.value})}
                    placeholder="Additional notes..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeTaskModal} disabled={savingTask}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={savingTask}>{savingTask ? 'Speichert…' : 'Create Task'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Log Grooming Modal */}
        {showLogModal && (
          <div className="modal-overlay" onClick={closeLogModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Log Grooming Completion</h2>
              {formErrorLog && <div className="shared-form-error-banner">{formErrorLog}</div>}
              <form onSubmit={handleLogGrooming}>
                <div className="form-group">
                  <UserSelect
                    value={newLog.user_id}
                    onChange={id => setNewLog({...newLog, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Task</label>
                  <select
                    value={newLog.task_id}
                    onChange={e => setNewLog({...newLog, task_id: e.target.value})}
                    required
                  >
                    <option value="">Select a task...</option>
                    {schedule.map(task => (
                      <option key={task.id} value={task.id}>{task.task}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newLog.notes}
                    onChange={e => setNewLog({...newLog, notes: e.target.value})}
                    placeholder="Any notes about this grooming session?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeLogModal} disabled={savingLog}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={savingLog}>{savingLog ? 'Speichert…' : 'Log Completion'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default GroomingSchedule;
